import React, { useState, useRef } from 'react';
import '../styles/OnboardingSection.css';

const faqs = [
  {
    id: 'faq-1',
    category: 'App & Login',
    question: 'Verification email not arriving after signing up',
    answer: 'Check your junk or spam folder first, verification emails occasionally land there. The email is sent from noreply@zellawellbeing.com, so add that address to your safe senders list if needed.\n\nIf it still has not arrived, you may have entered your email address incorrectly during sign-up. A typo is the most common cause. If the problem persists, contact hello@zellawellbeing.com.',
  },
  {
    id: 'faq-2',
    category: 'App & Login',
    question: 'Forgot your password',
    answer: 'On the login screen, tap the Forgot Password button. Enter the email address associated with your account and you will receive a reset link.\n\nIf the email does not arrive, make sure you are entering the correct email address and check your junk folder. The reset email is sent from noreply@zellawellbeing.com.',
  },
  {
    id: 'faq-3',
    category: 'App & Login',
    question: 'Forgot which email you used to sign up',
    answer: 'You will need to contact your internal Zella administrator. They can look up your account using your name and assist you in regaining access. Alternatively, you can create a new account with a different email.',
  },
  {
    id: 'faq-4',
    category: 'Surveys',
    question: 'Survey not submitting',
    answer: 'Surveys will not be recorded if your team has fewer than 5 members. This is a deliberate privacy threshold which ensures that no individual response can be identified.\n\nIf your team is below 5 members, your administrator will need to either add more team members or merge your team before survey data will be collected.',
  },
  {
    id: 'faq-5',
    category: 'Privacy',
    question: 'What data does Zella collect and who can see it?',
    answer: 'Surveys are completed individually and anonymously. Your individual responses are never visible to your employer or team manager.\n\nData is stored with only a team identification code and a company identification code, your name is never attached to a response. Company administrators can only see aggregated, team-level data: the average happiness score and a tiredness score across each Mon-Fri week. Individual breakdowns are not available to anyone.',
  },
];

const insightTips = [
  {
    title: 'Drops in happiness',
    description: 'A downward trend in the happiness score over consecutive weeks is an early signal of low morale or burnout. Even a small consistent drop warrants a check-in with the team.',
    severity: 'medium',
    label: 'Monitor closely',
  },
  {
    title: 'Rising tiredness score',
    description: 'A climbing tiredness score indicates the team is getting less sleep and feeling less alert. This can precede performance issues and increased accident risk.',
    severity: 'high',
    label: 'Act soon',
  },
  {
    title: 'Lines crossing on the graph',
    description: 'When the happiness line and tiredness line cross, particularly if tiredness overtakes happiness, this is a strong warning sign. Act quickly: consider workload reviews, schedule adjustments, or one-to-ones.',
    severity: 'critical',
    label: 'Act fast',
  },
  {
    title: 'Low survey engagement',
    description: 'The participation rate shows how many team members submitted surveys that week. A drop in submissions can itself signal disengagement, the data gap is the signal.',
    severity: 'medium',
    label: 'Monitor closely',
  },
];

const actionSteps = [
  {
    trigger: 'Happiness drops for 2+ consecutive weeks',
    action: 'Schedule a team check-in or anonymous feedback session. Review recent workload and any significant changes.',
  },
  {
    trigger: 'Tiredness score rises sharply',
    action: 'Review shift patterns, overtime, and on-call rotas. Consider adjusting workload distribution.',
  },
  {
    trigger: 'Lines cross: tiredness above happiness',
    action: 'Treat as urgent. Escalate to HR or senior management. Consider discussions with the team and wellbeing support.',
  },
  {
    trigger: 'Engagement drops',
    action: 'Remind the team about the app and reinforce privacy, employees may not trust the anonymity of the system.',
  },
];

const securityTips = [
  {
    title: 'Approve employees manually',
    description: 'Every join request must be approved by an admin. Never approve someone you do not recognise, verify their identity through an internal channel first.',
  },
  {
    title: 'Keep teams above 5 members',
    description: 'The 5-member minimum prevents a single person from skewing team averages. Small teams should be merged or supplemented before survey data is collected.',
  },
  {
    title: 'Review pending requests regularly',
    description: 'Check the Employee Requests section each week. Stale pending requests from unknown users should be rejected promptly.',
  },
  {
    title: 'Remove leavers immediately',
    description: 'When an employee leaves, remove them from the dashboard straight away. This prevents ex-employees from injecting data into your team averages.',
  },
];

// Generates the default template, inserting company code and name from Firebase
function buildDefaultTemplate(companyCode, companyName) {
  const code = companyCode || '[YOUR COMPANY CODE]';
  const name = companyName || '[COMPANY NAME]';

  return `Subject: We are launching Zella, please download the app

Hi team,

We are rolling out Zella at ${name}, a wellbeing app that helps us keep track of how the team is feeling over time. It takes about 30 seconds to complete a survey and your responses are completely anonymous; no one can see your individual answers, not me and not anyone else in the company.

Here is what you need to do:

1. Download the Zella app from the App Store or Google Play (search "Zella Fatigue & Wellbeing").

2. Create an account using your work email address and verify it. Check your junk folder if the verification email does not arrive.

3. Enter the company code: ${code}

4. Wait for approval; I will approve your request within [X] hours/days.

Once you are approved you will be assigned to your team and can start completing surveys straight away.

If you run into any issues, the app has a built-in Help section, or you can visit zellawellbeing.com/app-help. You can also email hello@zellawellbeing.com directly.

Thanks,
[YOUR NAME]`;
}

function EmailTemplate({ companyCode, companyName }) {
  const defaultText = buildDefaultTemplate(companyCode, companyName);
  const [emailText, setEmailText] = useState(defaultText);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef();

  // Keep in sync if companyData loads after mount (Firestore is async)
  const prevCode = useRef(companyCode);
  const prevName = useRef(companyName);
  if (prevCode.current !== companyCode || prevName.current !== companyName) {
    prevCode.current = companyCode;
    prevName.current = companyName;
    // Only patch placeholders — don't overwrite if user has already edited
    let updated = emailText;
    if (companyCode && updated.includes('[YOUR COMPANY CODE]')) {
      updated = updated.replace('[YOUR COMPANY CODE]', companyCode);
    }
    if (companyName && updated.includes('[YOUR NAME]')) {
      updated = updated.replace('[YOUR NAME]', companyName);
    }
    if (updated !== emailText) setEmailText(updated);
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(emailText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleReset = () => {
    if (window.confirm('Reset to the default template? Your edits will be lost.')) {
      setEmailText(buildDefaultTemplate(companyCode, companyName));
    }
  };

  return (
    <div className="email-template-wrap">
      <div className="email-template-box">
        <div className="email-template-header">
          <span className="email-template-label">Editable template</span>
          <div className="email-template-actions">
            <button className="email-reset-btn" onClick={handleReset}>Reset</button>
            <button className="email-copy-btn" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
        <textarea
          ref={textareaRef}
          className="email-template-textarea"
          value={emailText}
          onChange={(e) => setEmailText(e.target.value)}
          spellCheck
          rows={28}
        />
      </div>
      {companyCode && (
        <p className="email-code-note">
          Company code <strong>{companyCode}</strong> and company name have been inserted automatically. If you regenerate your code, check the template before sending.
        </p>
      )}
    </div>
  );
}

function IconDownload() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function IconMail() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  );
}

function IconBuilding() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <path d="M9 3v18M15 3v18M3 9h18M3 15h18"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5"/>
    </svg>
  );
}

function IconUsers() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}

function IconClipboard() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <path d="M12 11h4M12 16h4M8 11h.01M8 16h.01"/>
    </svg>
  );
}

function IconArrowDown() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <polyline points="19 12 12 19 5 12"/>
    </svg>
  );
}

const flowSteps = [
  {
    icon: <IconDownload />,
    actor: 'Employee',
    title: 'Download Zella',
    detail: 'Available on the App Store and Google Play. Create an account with a work or relevant email address.',
    color: '#3fb9eb',
    bg: 'rgba(63, 185, 235, 0.07)',
    border: 'rgba(63, 185, 235, 0.3)',
  },
  {
    icon: <IconMail />,
    actor: 'Employee',
    title: 'Create an account and verify email address',
    detail: 'A verification link is sent from noreply@zellawellbeing.com. Check junk if it does not arrive within a few minutes.',
    color: '#3fb9eb',
    bg: 'rgba(63, 185, 235, 0.07)',
    border: 'rgba(63, 185, 235, 0.3)',
  },
  {
    icon: <IconBuilding />,
    actor: 'Employee',
    title: 'Enter company code',
    detail: 'The company code can be found within the Overview tab. This code can be changed at any time and will not affect users who signed up with a previous code.',
    color: '#3fb9eb',
    bg: 'rgba(63, 185, 235, 0.07)',
    border: 'rgba(63, 185, 235, 0.3)',
  },
  {
    icon: <IconCheck />,
    actor: 'Admin',
    title: 'Approve and assign team',
    detail: 'Review the request in the dashboard. Verify the employee, select their team, and approve. They are immediately active.',
    color: '#1ab02a',
    bg: 'rgba(26, 176, 42, 0.07)',
    border: 'rgba(26, 176, 42, 0.25)',
    isAdmin: true,
  },
  {
    icon: <IconUsers />,
    actor: 'Team',
    title: 'Team reaches 5+ members',
    detail: 'Survey data is only collected once a team has at least 5 active members. This threshold protects individual anonymity.',
    color: '#ffa405',
    bg: 'rgba(255, 164, 5, 0.07)',
    border: 'rgba(255, 164, 5, 0.3)',
  },
  {
    icon: <IconClipboard />,
    actor: 'Employee',
    title: 'Surveys begin',
    detail: 'Employees complete short daily surveys. Their data feeds into your team averages on the Overview and Analytics screens.',
    color: '#3fb9eb',
    bg: 'rgba(63, 185, 235, 0.07)',
    border: 'rgba(63, 185, 235, 0.3)',
  },
];

function FlowChart() {
  return (
    <div className="flowchart">
      <div className="flowchart-legend">
        <span className="legend-item employee">Employee action</span>
        <span className="legend-item admin">Admin action</span>
        <span className="legend-item team">Team requirement</span>
      </div>

      <div className="flow-steps">
        {flowSteps.map((step, i) => (
          <React.Fragment key={i}>
            <div
              className={`flow-step ${step.isAdmin ? 'flow-step-admin' : ''}`}
              style={{ '--step-color': step.color, '--step-bg': step.bg, '--step-border': step.border }}
            >
              <div className="flow-step-left">
                <div className="flow-icon-wrap" style={{ background: step.bg, border: `1.5px solid ${step.border}`, color: step.color }}>
                  {step.icon}
                </div>
              </div>
              <div className="flow-step-body">
                <div className="flow-actor" style={{ color: step.color }}>{step.actor}</div>
                <h3 className="flow-title">{step.title}</h3>
                <p className="flow-detail">{step.detail}</p>
              </div>
              <div className="flow-step-num">{String(i + 1).padStart(2, '0')}</div>
            </div>

            {i < flowSteps.length - 1 && (
              <div className="flow-connector">
                <div className="flow-connector-line" />
                <div className="flow-connector-arrow">
                  <IconArrowDown />
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="callout callout-info" style={{ marginTop: '1.75rem' }}>
        <div className="callout-bar" />
        <div>
          <strong>Team size requirement</strong>
          <p>Teams must have at least 5 active members before survey data is recorded. If a team is below this threshold, surveys are not stored or shown on the dashboard.</p>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(!open)}>
        <span>{faq.question}</span>
        <span className="faq-chevron">{open ? '\u2212' : '+'}</span>
      </button>
      {open && (
        <div className="faq-answer">
          {faq.answer.split('\n\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OnboardingSection({ companyData }) {
  const [activeTab, setActiveTab] = useState('getstarted');

  const companyCode = companyData?.companyCode ?? null;

  const tabs = [
    { id: 'getstarted', label: 'Get Started' },
    { id: 'insights',   label: 'Reading Insights' },
    { id: 'security',   label: 'Data Security' },
    { id: 'email',      label: 'Onboarding Email' },
    { id: 'faqs',       label: 'FAQs' },
  ];

  const faqsByCategory = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) acc[faq.category] = [];
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div className="onboarding-section">
      <div className="dashboard-header">
        <div>
          <h1>Onboarding</h1>
          <p>Everything you need to get your team set up, understand your data, and take action when it matters.</p>
        </div>
      </div>

      <div className="onboarding-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`onboarding-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="onboarding-content">

        {activeTab === 'getstarted' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h2>How employees get set up</h2>
              <p>Share these steps with your team. Most employees are active within a few minutes of downloading the app.</p>
            </div>
            <FlowChart />
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h2>Reading your dashboard data</h2>
              <p>Zella surfaces two core metrics each week: happiness and tiredness. Here is what to look for and when to act.</p>
            </div>

            <div className="insight-list">
              {insightTips.map((tip, i) => (
                <div className={`insight-row severity-${tip.severity}`} key={i}>
                  <div className="insight-row-left">
                    <span className={`severity-pill severity-${tip.severity}`}>{tip.label}</span>
                  </div>
                  <div className="insight-row-body">
                    <h3>{tip.title}</h3>
                    <p>{tip.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="panel-subheader">
              <h2>What to do when scores change</h2>
              <p>A quick reference for responding to shifts in your team data.</p>
            </div>

            <div className="action-table">
              <div className="action-table-header">
                <span>Trigger</span>
                <span>Recommended action</span>
              </div>
              {actionSteps.map((row, i) => (
                <div className="action-table-row" key={i}>
                  <span className="action-trigger">{row.trigger}</span>
                  <span className="action-response">{row.action}</span>
                </div>
              ))}
            </div>

            <div className="callout callout-success">
              <div className="callout-bar" />
              <div>
                <strong>What good looks like</strong>
                <p>Stable or rising happiness with a stable or falling tiredness score, week on week. The two lines should remain clearly separated with happiness consistently above tiredness. Aim for high participation to ensure the data is representative of your team.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h2>Keeping your data secure</h2>
              <p>As an admin, you are the gatekeeper. These steps prevent unauthorised users from joining your company and corrupting your team data.</p>
            </div>

            <div className="security-grid">
              {securityTips.map((tip, i) => (
                <div className="security-card" key={i}>
                  <div className="security-card-index">{String(i + 1).padStart(2, '0')}</div>
                  <h3>{tip.title}</h3>
                  <p>{tip.description}</p>
                </div>
              ))}
            </div>

            <div className="callout callout-info">
              <div className="callout-bar" />
              <div>
                <strong>How Zella protects your data</strong>
                <p>All join requests require explicit admin approval. No one can join your company without you granting access. Survey data is stored against team and company IDs only, never names. Individual responses are never accessible to admins. The 5-member minimum further protects against de-anonymisation.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h2>Onboarding email template</h2>
              <p>Edit the template below and copy it to send to your team. Your company code and name have been filled in automatically.</p>
            </div>
            <EmailTemplate companyCode={companyCode} companyName={companyData?.name} />
          </div>
        )}

        {activeTab === 'faqs' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h2>Frequently asked questions</h2>
              <p>Common issues and how to resolve them.</p>
            </div>

            {Object.entries(faqsByCategory).map(([category, items]) => (
              <div className="faq-group" key={category}>
                <h3 className="faq-category-label">{category}</h3>
                <div className="faq-list">
                  {items.map(faq => (
                    <FAQItem key={faq.id} faq={faq} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}